import moment from 'moment';
import { FC, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Spinner } from '@common/components/Spinner';
import {
  useGetSpaSettings,
  useGetSpaTreatment,
  useGetSpaTreatmentTimeslots,
  useTimeslotAvailability,
  useBookingFormLabels,
} from '@features/spa/hooks';
import { mapSlotsToTimeslotChips } from '@features/spa/utils/mapSlotsToTimeslotChips';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { Property } from 'shared-types/Property';

import type { SpaGuestConfig } from 'voyager';
import { SpaTreatmentFacilityBookingForm } from 'voyager';

import { SpaTreatmentFormValues } from './SpaTreatmentBookingV2.types';
import { useSubmitTreatment } from './hooks/useSubmitTreatment';

type SpaTreatmentBookingV2Props = {
  id: number;
  property: Property;
  language: string;
  onClose: (showMiniBasket: boolean) => void;
};

export const SpaTreatmentBookingV2: FC<SpaTreatmentBookingV2Props> = ({
  id,
  property,
  language,
  onClose,
}) => {
  const { t } = useTranslations();
  const bookingLabels = useBookingFormLabels(language);
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams) as any;
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  );

  const form = useFormContext<SpaTreatmentFormValues>();

  const { data: spaEntity, isFetching: isLoadingEntity } = useGetSpaTreatment(
    property.id,
    language,
    id,
    moment(journeyParams.spaDate).format('YYYY-MM-DD')
  );
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const showGenderNeutral = spaSettings?.showGenderNeutral || false;

  const watchedGuests = useWatch({ control: form.control, name: 'guests' });
  const watchedDate = useWatch({ control: form.control, name: 'date' });
  const watchedArrivalTime = useWatch({ control: form.control, name: 'arrivalTime' });

  const selectedDateString = (
    watchedDate || moment(journeyParams.spaDate).format('YYYY-MM-DD')
  ).trim();

  const [weekStartDate, setWeekStartDate] = useState<string>(() => {
    const d = moment(selectedDateString);
    return d.clone().startOf('isoWeek').format('YYYY-MM-DD');
  });

  const {
    data: rawTimeslots,
    isFetching: isLoadingTimeslots,
    isError: isTimeslotsError,
  } = useGetSpaTreatmentTimeslots(property.id, Number(id), weekStartDate, language);

  const { slotsByDate, availability, handleDateSelect, handleWeekNavigate } =
    useTimeslotAvailability({
      rawTimeslots,
      selectedDateString,
      weekStartDate,
      form,
      onWeekNavigate: setWeekStartDate,
    });

  const timeslotChips = useMemo(
    () => mapSlotsToTimeslotChips(slotsByDate, selectedDateString),
    [slotsByDate, selectedDateString]
  );

  const minGuests = spaEntity?.minimumGuests || 1;
  const maxGuests = spaEntity?.maximumGuests || minGuests;
  const requestedGuests = journeyParams?.adults || 1;
  const guestCount = Math.max(minGuests, Math.min(maxGuests, requestedGuests));

  const guestConfigs: SpaGuestConfig[] = useMemo(() => {
    const guests = watchedGuests || [];
    return Array.from({ length: guestCount }, (_, i) => ({
      guestNumber: i + 1,
      values: {
        title: guests[i]?.title || '',
        firstName: guests[i]?.firstName || '',
        lastName: guests[i]?.lastName || '',
        gender: guests[i]?.gender || '',
      },
      errors: {},
    }));
  }, [watchedGuests, guestCount]);

  const genderOptions = useMemo(() => {
    const options = [
      { value: 'male', label: t('common.male') },
      { value: 'female', label: t('common.female') },
    ];
    if (showGenderNeutral) {
      options.push({ value: 'neutral', label: t('common.genderNeutral') });
    }
    return options;
  }, [showGenderNeutral, t]);

  const handleGuestChange = (guestIndex: number, field: string, value: string) => {
    const currentGuests = form.getValues('guests') || [];
    const updatedGuests = [...currentGuests];
    if (!updatedGuests[guestIndex]) {
      updatedGuests[guestIndex] = { title: '', firstName: '', lastName: '', gender: '' };
    }
    updatedGuests[guestIndex] = { ...updatedGuests[guestIndex], [field]: value };
    form.setValue('guests', updatedGuests);
  };

  const handleTimeslotSelect = (timeslotValue: string) => {
    const daySlots = slotsByDate[selectedDateString];
    if (!daySlots) return;
    const slot = daySlots.find((s) => moment(s.startDateTime).format('HH:mm') === timeslotValue);
    if (slot) {
      form.setValue('arrivalTime', {
        startTime: moment(slot.startDateTime).format('HH:mm'),
        endTime: moment(slot.endDateTime).format('HH:mm'),
        startDateTime: moment(slot.startDateTime).format(),
        endDateTime: moment(slot.endDateTime).format(),
      });
    }
  };

  const hasExtras = !!(spaEntity?.extras?.length || spaEntity?.selectionGroups?.length);

  const { handleSubmit, handleNextStep, handleBack } = useSubmitTreatment({
    form,
    spaEntity: spaEntity || undefined,
    propertyId: property.id,
    language,
    id,
    onClose,
    hasExtras,
  });

  const basePrice = spaEntity?.price?.amount || 0;
  const currencyCode = spaEntity?.price?.currencyCode || 'GBP';

  if (isLoadingEntity && !spaEntity) {
    return <Spinner isActive showDelayMessage />;
  }

  const isTelephoneOnly = spaEntity?.telephoneOnly || false;
  const hasNoAvailability =
    !isLoadingTimeslots &&
    (isTimeslotsError ||
      (rawTimeslots && Array.isArray(rawTimeslots) && rawTimeslots.length === 0));

  if (isTelephoneOnly) {
    return (
      <SpaTreatmentFacilityBookingForm
        imageUrl={spaEntity?.image?.url}
        imageAlt={spaEntity?.name}
        onBack={handleBack}
        isLoading={false}
        guestCount={guestCount}
        minGuests={guestCount}
        maxGuests={guestCount}
        onGuestCountChange={() => {}}
        activeDate={weekStartDate}
        selectedDate={selectedDateString}
        availability={[]}
        onDateSelect={() => {}}
        onWeekNavigate={() => {}}
        timeslots={[]}
        onTimeslotSelect={() => {}}
        guests={[]}
        onExistingGuestSelect={() => {}}
        onGuestChange={() => {}}
        titleOptions={[]}
        genderOptions={[]}
        preferencesEnabled={false}
        onPreferencesToggle={() => {}}
        preferenceCategories={[]}
        expandedPreferences={[]}
        onPreferenceCategoryToggle={() => {}}
        onPreferenceOptionToggle={() => {}}
        onPreferenceOtherChange={() => {}}
        onPreferenceFreeTextChange={() => {}}
        labels={{
          title: spaEntity?.name || '',
          subtitle: t('spa.telephoneOnly', { spaEntity: t('spa.spaTreatment').toLowerCase() }),
          ...bookingLabels,
        }}
      />
    );
  }

  return (
    <>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <SpaTreatmentFacilityBookingForm
        imageUrl={spaEntity?.image?.url}
        imageAlt={spaEntity?.name}
        onBack={handleBack}
        isLoading={isLoadingTimeslots}
        guestCount={guestCount}
        minGuests={spaEntity?.minimumGuests || 1}
        maxGuests={spaEntity?.maximumGuests || spaEntity?.minimumGuests || 1}
        onGuestCountChange={() => {}}
        activeDate={weekStartDate}
        selectedDate={selectedDateString}
        availability={availability}
        onDateSelect={handleDateSelect}
        onWeekNavigate={handleWeekNavigate}
        timeslots={hasNoAvailability ? [] : timeslotChips}
        selectedTimeslot={watchedArrivalTime?.startTime}
        onTimeslotSelect={handleTimeslotSelect}
        guests={hasNoAvailability ? [] : guestConfigs}
        onExistingGuestSelect={() => {}}
        onGuestChange={handleGuestChange}
        titleOptions={['Mr', 'Mrs', 'Ms', 'Miss', 'Dr']}
        genderOptions={genderOptions}
        preferencesEnabled={false}
        onPreferencesToggle={() => {}}
        preferenceCategories={[]}
        expandedPreferences={[]}
        onPreferenceCategoryToggle={() => {}}
        onPreferenceOptionToggle={() => {}}
        onPreferenceOtherChange={() => {}}
        onPreferenceFreeTextChange={() => {}}
        labels={{
          title: spaEntity?.name || '',
          subtitle: hasNoAvailability ? t('list.noAvailability') : t('spa.booking.subtitle'),
          noteText: t('spa.package.arrivalTime'),
          ...bookingLabels,
          stickyFooter: {
            totalLabel: bookingLabels.stickyFooterTotalLabel,
            ctaLabel: hasExtras ? t('spa.package.nextStep') : t('spa.book'),
          },
        }}
        stickyFooter={
          hasNoAvailability
            ? undefined
            : {
                title: spaEntity?.name || '',
                totalAmount: basePrice * guestCount,
                currencyCode,
                onCtaClick: hasExtras ? handleNextStep : handleSubmit,
              }
        }
      />
    </>
  );
};
